import { Loader, useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { GatewayEndorsementService as LoadSaveService } from 'twia-capability-gateway-policychange';
import { useTranslator, IntlContext } from '@jutro/locale';
import _ from 'lodash';
import React, {
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import TWIADateFormatUtil from 'ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil';
import Echeck from 'twia-capability-quoteandbind-residential-react/components/Echeck/Echeck';
import { CustomQuoteService } from 'twia-capability-quoteandbind-residential';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil';
import metadata from './PaymentDetailsPage.metadata.json5';
import messages from './PaymentDetails.messages';
import styles from './PaymentDetails.module.scss';

function TWIAChangePaymentDetailsPage(props) {
    const {
        wizardData: submissionVM,
        updateWizardData
    } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const intlContext = useContext(IntlContext);
    const { showAlert, showConfirm } = useModal();
    const history = useHistory();
    const jobID = submissionVM.jobID.value;
    const sessionID = submissionVM.sessionUUID.value;
    const totalCost = _.get(submissionVM, 'totalCost.amount.value');
    const previousTotal = _.get(submissionVM, 'previousTotalCost.amount.value');
    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate.value`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate.value`);
    const eftEnabled = _.get(submissionVM, 'bindData.eftEnabled_TWIA.value');
    const changeInCost = totalCost - previousTotal;
    const increase = changeInCost > 0;
    const translator = useTranslator();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showError, setShowError] = useState(false);
    const { authHeader } = useAuthentication();
    const [showSubmitLoader, setShowSubmitLoader] = useState(false);
    const { isComponentValid, disregardFieldValidation, onValidate } =
        useValidation('TWIAChangePaymentDetailsPage');

    const getSelectedPaymentMethod = useCallback(() => (!eftEnabled
        // eslint-disable-next-line no-secrets/no-secrets
        ? _.get(submissionVM, `bindData.paymentDetails.nonEFTPaymentMethod_TWIA.value.code`)
        // eslint-disable-next-line no-secrets/no-secrets
        : _.get(submissionVM, `bindData.paymentDetails.paymentMethod_TWIA.value.code`)),
        [eftEnabled, submissionVM]);

    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const resetElectronicPaymentData = useCallback(() => {
        const epayData = _.get(submissionVM, 'bindData.paymentDetails.electronicPaymentData_TWIA');

        if (epayData && epayData.aspects && !epayData.aspects.subtreeValid) {
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.electronicPaymentData_TWIA.value', null);
            updateWizardData(submissionVM);
        }
    }, [submissionVM, updateWizardData]);

    useEffect(() => {
        if (changeInCost > 0) {
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.paymentMethod_TWIA.value',
                undefined);
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.nonEFTPaymentMethod_TWIA.value',
                undefined);
        } else {
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.paymentMethod_TWIA.value',
                'invoice');
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.nonEFTPaymentMethod_TWIA.value',
                'invoice');
        }
    }, [])

    const writeValue = useCallback((value, path) => {

        if(_.includes(path,'paymentDetails') && !_.includes(path,'bindData')){
            _.set(submissionVM, `bindData.${path}`, value);
        } else {
            _.set(submissionVM, path, value);
        }

        if (value !== 'echeck' && _.includes(path, 'Method')) {
            disregardFieldValidation('Echeck');
            disregardFieldValidation('TWIAAddressComponent');
        }

        updateWizardData(submissionVM);
    }, [disregardFieldValidation, submissionVM, updateWizardData]);

    const handleNext = useCallback(() => {
        showConfirm({
            title: messages.confirmTransaction,
            message: messages.confirmTransactionMsg,
            status: 'warning',
            icon: 'gw-help',
            confirmButtonText: commonMessages.yesModel,
            cancelButtonText: commonMessages.noModel,
            // eslint-disable-next-line consistent-return
        }).then((results) => {
            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }

            setShowSubmitLoader(true);

            const checkUwIssues = CustomQuoteService.getUWIssueStatus(jobID, sessionID,
                authHeader);

            checkUwIssues.then((result) => {
                if (result.anyPolicyHoldUWIssues) {
                    setShowSubmitLoader(false);
                    showAlert({
                        title: '',
                        message: messages.uwPolicyHold_twia,
                        status: 'error',
                        icon: 'mi-error-outline',
                        cancelButtonText: commonMessages.cancelModel,
                    }).then((res) => {
                        if (res === 'confirm') {
                           // 2024-12-29 - Debra Frum - DE17673 - Suppress the Exit confirmation popup
                           submissionVM.noCancelWarning = true;
                           history.push(`${process.env.PUBLIC_PATH}/change/${submissionVM.jobID.value}/summary`)
                        }
                    })
                }
                else if (result.anyOtherBlockingUWIssues) {
                    setShowSubmitLoader(false);
                    showAlert({
                        title: '',
                        message: messages.uwOtherBlockingIssue_twia,
                        status: 'error',
                        icon: 'mi-error-outline',
                        cancelButtonText: commonMessages.cancelModel,
                    }).then((res) => {
                        if (res === 'confirm') {
                            // 2024-12-29 - Debra Frum - DE17673 - Suppress the Exit confirmation popup
                            submissionVM.noCancelWarning = true;
                            history.push(`${process.env.PUBLIC_PATH}/change/${submissionVM.jobID.value}/summary`)
                        }
                    })
                }
                else {
                    const job = _.cloneDeep(submissionVM.value);

                    if (getSelectedPaymentMethod() !== 'echeck') {
                        job.bindData.paymentDetails.electronicPaymentData_TWIA = null
                    }

                    const submit = LoadSaveService.payAndBind_TWIA(jobID,
                        job.bindData.paymentDetails, authHeader);

                    submit.then((response) => {
                        const electronicData = submissionVM.bindData.paymentDetails
                            .electronicPaymentData_TWIA.value;

                        submissionVM.value = response;
                        submissionVM.bindData.paymentDetails.electronicPaymentData_TWIA.value =
                            electronicData || {}
                        // 2024-12-29 - Debra Frum - DE17673 - Suppress the Exit confirmation popup
                        submissionVM.noCancelWarning = true;
                        updateWizardData(submissionVM);
                        setShowSubmitLoader(false);
                        history.push({
                            pathname: '/endorsementConfirmation',
                            state: {
                                response,
                                authHeader
                            },
                        })
                    }).catch((err) => {
                        setShowSubmitLoader(false);
                        // DE17798
                        
                        const errorMessage = _.split(err.baseError, 'ErrorMessage: ')[1];
                        const errorCode = _.get(err, 'appErrorCode');

                        if (errorMessage.includes("greaterThanMax")) {
                            showAlert({
                                title: messages.paymentThreshold,
                                message: messages.paymentThresholdMsgR352,
                                status: 'error',
                                icon: 'mi-error-outline',
                                confirmButtonText: commonMessages.ok,
                            })
                        } else if (errorMessage.includes("Getting Time Out to Pay and Issue process for job") ||
                            errorMessage.includes("Error occurred during Pay and Issue process for job")) {
                            showAlert({
                                title: commonMessages.genericError,
                                message: messages.unableToProcessPayment,
                                status: 'error',
                                icon: 'mi-error-outline',
                                confirmButtonText: commonMessages.ok,
                            })
                        } else {
                            switch (errorCode) {
                                case 10075:
                                    showAlert({
                                        title: messages.invalidBank,
                                        message: messages.badBank,
                                        status: 'error',
                                        icon: 'mi-error-outline',
                                        confirmButtonText: commonMessages.ok,
                                    })
                                    break;
                                case 10074:
                                    showAlert({
                                        title: messages.paymentThreshold,
                                        message: messages.paymentThresholdMsg,
                                        status: 'error',
                                        icon: 'mi-error-outline',
                                        confirmButtonText: commonMessages.ok,
                                    })
                                    break;
                                    case 602: // DE18570 - Adding Error Pop-up Details
                                        setShowSubmitLoader(false);
                                        showAlert({
                                            title: messages.unableToQuote,
                                            message: messages.TWIAIssueErrorMessage,
                                            status: 'error',
                                            icon: 'mi-error-outline',
                                            cancelButtonText: commonMessages.close,
                                        }).then((res) => {
                                            if (res === 'confirm') {
                                                // 2024-12-29 - Debra Frum - DE17673 - Suppress the Exit confirmation popup
                                                submissionVM.noCancelWarning = true;
                                                // DE15535 
                                                history.push(`${process.env.PUBLIC_PATH}/change/${submissionVM.jobID.value}/summary`)
                                            }
                                        })
                                        break;
                                default:
                                    ErrorPopupUtil.showSaveErrorPopup(showAlert, true);
                                    break;
                            }
                        }

                        return false;
                    });
                }
            }, _.noop)
        })
    }, [authHeader, getSelectedPaymentMethod, history, jobID, sessionID,
        showAlert, showConfirm, submissionVM, updateWizardData]);

    const diffCost = useCallback(() => {
        const formattedDiffCost = intlContext.formatNumber(changeInCost, {
            style: 'currency',
            currency: 'USD'
        });

        if (increase) {
            return `+${formattedDiffCost}`;
        }

        return formattedDiffCost;
    }, [changeInCost, increase, intlContext]);

    const overrideProps = {
        '@field': {
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        changeInPremium: {
            value: diffCost()
        },
        proposedPolicyPeriod: {
            value: formatPolicyPeriod(periodStartDate, periodEndDate)
        },
        creditDueMessage: {
            visible: changeInCost < 0,
            content: translator(messages.creditDue)
        },
        distributedMessage: {
            visible: changeInCost > 0 && getSelectedPaymentMethod() === 'invoice',
            content: translator(messages.differenceDistributed)
        },
        paymentInfo: {
            visible: changeInCost > 0
        },
        paymentMethodWithEft: {
            visible: eftEnabled
        },
        paymentMethod: {
            visible: !eftEnabled
        },
        echeckDetails: {
            // eslint-disable-next-line no-secrets/no-secrets
            visible: !_.isUndefined(_.get(submissionVM, 'bindData.paymentDetails.electronicPaymentData_TWIA.value'))
                && (getSelectedPaymentMethod() === 'echeck') && changeInCost > 0,
            bindDataVM: _.get(submissionVM, 'bindData'),
            writeValue,
            onValidate,
            showError
        },
    };

    const resolvers = {
        resolveComponentMap: {
            echeckDetailsComponent: Echeck
        },
        resolveClassNameMap: styles
    };

    return (
        <WizardPage
            nextLabel={messages.paymentBuyNow}
            showNext={!showSubmitLoader}
            showPrevious={_.get(submissionVM, 'status.value') === 'Submitted_Ext'
                ? false : !showSubmitLoader}
            showCancel={!showSubmitLoader}
            hideHeader={showSubmitLoader}
            onNext={handleNext}
            onPrevious={resetElectronicPaymentData}
            disableNext={!isComponentValid}
        >
            {!showSubmitLoader ?
                (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={submissionVM}
                        onModelChange={updateWizardData}
                        onValueChange={writeValue}
                        onValidationChange={onValidate}
                        overrideProps={overrideProps}
                        classNameMap={resolvers.resolveClassNameMap}
                        showErrors={showError}
                        componentMap={resolvers.resolveComponentMap}
                    />
                ) :
                (<Loader
                    loaded={!showSubmitLoader}
                    text="Processing Transaction..."
                />)}
        </WizardPage>
    );
}

TWIAChangePaymentDetailsPage.propTypes = wizardProps;
export default TWIAChangePaymentDetailsPage;